import type { DateValue } from "@internationalized/date";

/**
 * Converts a DateValue-based date range to ISO string format for API requests.
 *
 * @param dateRange - The date range with DateValue objects or null/undefined
 * @returns An object with 'from' and 'to' ISO date strings, or undefined if no range
 */
export function dateRangeToApiFormat(
  dateRange: { start: DateValue; end: DateValue } | null | undefined,
): { from: string; to: string } | undefined {
  if (!dateRange) return undefined;

  return {
    from: dateRange.start.toString(),
    to: dateRange.end.toString(),
  };
}

// Formatted from the local calendar fields rather than through toISOString, which
// converts to UTC first: west of Greenwich that returns a day the viewer has not
// reached yet for the last hours of the evening.
export const getStartDateISO = (days: number, now: Date = new Date()): string => {
  const d = new Date(now);
  d.setDate(d.getDate() - days);

  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
};

export const toDateOrNull = (value: string | null, nullable = true): Date | null => {
  if (!value) {
    if (nullable) {
      return null;
    }
    throw new Error("Date value is required when nullable is false");
  }

  return new Date(value);
};

export const formatShortDate = (date: string | Date | null | undefined) => {
  if (!date) return "N/A";
  const d = new Date(date);
  const month = d.toLocaleDateString("en-US", { month: "short" });
  return `${month} ${d.getDate()}`;
};

export const formatDate = (date: string | Date | null | undefined) => {
  if (!date) return "N/A";
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// "3h ago" under a day old, "3d ago" from then on.
export const formatPostedAgo = (date: string | Date | null | undefined) => {
  if (!date) return "N/A";
  const d = new Date(date);
  const hours = Math.max(0, Math.floor((Date.now() - d.getTime()) / 3_600_000));
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

// "1h", "8h", "2d" — hours/days until a future date, rounded to whole hours with a floor of 1h (the
// worker that advances sequence steps only ticks hourly, see processSequenceRuns). Used for
// "Re-scan in X".
// Null once the date has passed: the caller has nothing truthful left to say about a wait that is
// already over, and should render nothing rather than a stale countdown.
export const formatDurationUntil = (date: string | Date): string | null => {
  const diffMs = new Date(date).getTime() - Date.now();
  if (diffMs <= 0) return null;
  const hours = Math.max(1, Math.round(diffMs / 3_600_000));
  if (hours < 24) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
};

export const formatRelativeDay = (date: string | Date) => {
  const d = new Date(date);
  const elapsedMs = Date.now() - d.getTime();
  const days = Math.floor(elapsedMs / 86_400_000);
  if (days > 30) return `on ${d.toLocaleDateString("en-GB", { day: "numeric", month: "long" })}`;

  const relative = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  const hours = Math.floor(elapsedMs / 3_600_000);
  return hours < 24 ? relative.format(-hours, "hour") : relative.format(-days, "day");
};
